import type { GitChangeStatus, GitDiffManifest } from "../../shared/git-manifest";

export type CodeArea = "application" | "tests" | "data" | "infrastructure" | "configuration" | "documentation" | "generated";

export interface DerivedCodeFile {
  additions?: number;
  area: CodeArea;
  changedLines?: number;
  deletions?: number;
  path: string;
  status: GitChangeStatus;
}

export interface DerivedCodeArea {
  additions?: number;
  changedLines?: number;
  deletions?: number;
  files: DerivedCodeFile[];
  id: CodeArea;
  label: string;
}

export interface DerivedCodeBreakdown {
  areas: DerivedCodeArea[];
  files: DerivedCodeFile[];
  source: "git-manifest" | "changed-paths";
}

const areaOrder: CodeArea[] = ["application", "tests", "data", "infrastructure", "configuration", "documentation", "generated"];

const areaLabels: Record<CodeArea, string> = {
  application: "Application code",
  tests: "Tests and proof",
  data: "Schema and data",
  infrastructure: "Infrastructure",
  configuration: "Configuration",
  documentation: "Documentation",
  generated: "Generated output",
};

export function codeAreaForPath(path: string): CodeArea {
  const normalized = path.toLocaleLowerCase();
  const fileName = normalized.split("/").at(-1) ?? normalized;
  if (/(^|\/)(test|tests|spec|specs|__tests__|fixtures?|snapshots?)(\/|$)/.test(normalized)
    || /(^|\/)[^/]*(tests?|specs?)(\/|$)/.test(normalized)
    || /(?:^|\.)((?:test|spec)|snap)(?:\.|$)/.test(fileName)) return "tests";
  if (/(^|\/)(migrations?|schema|schemas|seed|seeds|database|db|dataform)(\/|$)/.test(normalized)
    || /\.(sql|sqlx|prisma)$/.test(fileName)) return "data";
  if (/(^|\/)(infra|infrastructure|iac|terraform)(\/|$)/.test(normalized)
    || /\.(tf|tfvars)$/.test(fileName)) return "infrastructure";
  if (/(^|\/)(docs?|documentation)(\/|$)/.test(normalized)
    || /\.(md|mdx|rst|adoc)$/.test(fileName)) return "documentation";
  if (/(^|\/)(generated|vendor|dist|build|coverage)(\/|$)/.test(normalized)
    || /(?:\.min\.(?:js|css)|\.map|\.lock)$/.test(fileName)
    || /^(package-lock\.json|pnpm-lock\.yaml|yarn\.lock)$/.test(fileName)) return "generated";
  if (/(^|\/)(config|configs|\.github)(\/|$)/.test(normalized)
    || /(^|\.)(config|rc)\.[^.]+$/.test(fileName)
    || /\.(ya?ml|toml|ini|env)$/.test(fileName)) return "configuration";
  return "application";
}

function inferredStatus(path: string, snapshots?: Record<string, { base: string; head: string; oldPath?: string }>): GitChangeStatus {
  const snapshot = snapshots?.[path];
  if (!snapshot) return "unknown";
  if (snapshot.oldPath && snapshot.oldPath !== path) return "renamed";
  if (!snapshot.base && snapshot.head) return "added";
  if (snapshot.base && !snapshot.head) return "deleted";
  return "modified";
}

function sumKnown(files: DerivedCodeFile[], field: "additions" | "deletions" | "changedLines") {
  const values = files.map((file) => file[field]).filter((value): value is number => value !== undefined);
  return values.length > 0 ? values.reduce((total, value) => total + value, 0) : undefined;
}

export function deriveCodeBreakdown({
  changedFiles,
  manifest,
  snapshots,
}: {
  changedFiles: ReadonlyArray<{ path: string; changedLines?: number }>;
  manifest?: GitDiffManifest;
  snapshots?: Record<string, { base: string; head: string; oldPath?: string }>;
}): DerivedCodeBreakdown {
  const files: DerivedCodeFile[] = manifest?.files.length
    ? manifest.files.map((file) => ({
        additions: file.additions ?? undefined,
        area: codeAreaForPath(file.path),
        changedLines: file.additions === null || file.deletions === null ? undefined : file.additions + file.deletions,
        deletions: file.deletions ?? undefined,
        path: file.path,
        status: file.status,
      }))
    : changedFiles.map((file) => ({
        area: codeAreaForPath(file.path),
        changedLines: file.changedLines,
        path: file.path,
        status: inferredStatus(file.path, snapshots),
      }));

  files.sort((left, right) => (right.changedLines ?? -1) - (left.changedLines ?? -1) || left.path.localeCompare(right.path));
  const areas = areaOrder.flatMap((id): DerivedCodeArea[] => {
    const grouped = files.filter((file) => file.area === id);
    return grouped.length === 0 ? [] : [{
      additions: sumKnown(grouped, "additions"),
      changedLines: sumKnown(grouped, "changedLines"),
      deletions: sumKnown(grouped, "deletions"),
      files: grouped,
      id,
      label: areaLabels[id],
    }];
  });
  return { areas, files, source: manifest?.files.length ? "git-manifest" : "changed-paths" };
}
